/**
 * Authentication service.
 * Wraps Supabase Auth for login, logout, session management.
 */

let currentUser = null;
let currentRole = null;
const authChangeCallbacks = [];

export async function initAuth(supabase) {
  const { data: { session } } = await supabase.auth.getSession();

  if (session) {
    currentUser = session.user;
    await fetchRole(supabase);
  }

  supabase.auth.onAuthStateChange(async (event, session) => {
    currentUser = session?.user || null;
    if (currentUser) {
      try {
        await fetchRole(supabase);
      } catch (err) {
        console.warn('fetchRole failed during auth state change:', err.message);
      }
    } else {
      currentRole = null;
    }
    authChangeCallbacks.forEach(cb => cb({ event, session }));
  });
}

async function fetchRole(supabase) {
  const { data } = await supabase
    .from('pilots')
    .select('role')
    .eq('id', currentUser.id)
    .single();

  const role = data?.role;
  currentRole = (role === 'admin' || role === 'owner' || role === 'pilot') ? role : 'pilot';
}

export function getUser() {
  return currentUser;
}

export function getRole() {
  return currentRole;
}

export function onAuthChange(callback) {
  authChangeCallbacks.push(callback);
}

export function isAdmin() {
  return currentRole === 'admin' || currentRole === 'owner';
}

export function isOwner() {
  return currentRole === 'owner';
}

export async function login(supabase, email, password) {
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password,
  });
  if (error) throw error;

  // Check if the pilot account is active
  const { data: pilot } = await supabase
    .from('pilots')
    .select('active, role')
    .eq('id', data.user.id)
    .single();

  if (pilot && pilot.active === false) {
    await supabase.auth.signOut();
    currentUser = null;
    currentRole = null;
    throw new Error('Your account has been deactivated. Contact an administrator.');
  }

  // Eagerly set user + role so the router and sidebar have them
  // immediately when navigate() fires after login returns.
  // onAuthStateChange will re-confirm the same values asynchronously.
  currentUser = data.user;
  const role = pilot?.role;
  currentRole = (role === 'admin' || role === 'owner' || role === 'pilot') ? role : 'pilot';

  return data;
}

export async function logout(supabase) {
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
  currentUser = null;
  currentRole = null;
}
